'use client'

import { useCallback, useEffect, useRef, useState, FormEvent } from 'react'
import Image from 'next/image'
import { Input, marketingFieldClasses } from '@/components/marketing/Input'
import { Button } from '@/components/ui/button'
import { reopenConsentBanner } from '@/lib/consent-manager'
import { canonicalizeDomainInput } from '@/lib/sanitize'
import './radar-arabic.css'
import {
  AUDIT_STEPS,
  CAPABILITIES,
  FAQ_ITEMS,
  LANGUAGE_LABELS,
  LANGUAGE_OPTIONS,
  MARKET_LABELS,
  MARKET_OPTIONS,
  MODELS,
  PROMPT_LIBRARY,
  PROMPT_TABS,
  RESPONSE_LIBRARY,
  type LanguageKey,
  type MarketKey,
  type ModelKey,
  type PromptKey,
} from './radar-arabic-data'

/**
 * Arabic Radar landing page.
 *
 * Ported from `docs/design-references/radar-arabic.html`: section order, class names,
 * and copy match it so the two can be diffed. Styling is the sibling
 * `radar-arabic.css`, scoped under `.radar-ar`.
 *
 * Deviations from the mock, all deliberate:
 *   - Its topbar, mobile menu, and footer are dropped. This page carries no
 *     global navigation, just the wordmark home link and an English language
 *     switcher to /platform, the equivalent Radar content.
 *   - The lead form posts to `/api/contact` instead of doing nothing. Market,
 *     audit language, domain, and role are composed into the message so the
 *     configuration reaches the inbox with the lead.
 *   - Its `track()` helper wrote events to localStorage and the console. That
 *     is dropped rather than half-wired; the site's root consent-gated
 *     analytics lifecycle remains responsible for measurement.
 *   - Model responses render as text segments rather than injected HTML —
 *     `dangerouslySetInnerHTML` is banned repo-wide (CLAUDE.md §6).
 */

const HOME_HREF = '/'
const ENGLISH_RADAR_HREF = '/platform'

export default function RadarArabicContent() {
  const [market, setMarket] = useState<MarketKey>('saudi')
  const [language, setLanguage] = useState<LanguageKey>('bilingual')
  const [promptKey, setPromptKey] = useState<PromptKey>('discover')
  const [model, setModel] = useState<ModelKey>('ChatGPT')

  const [domain, setDomain] = useState('')
  const [domainStatus, setDomainStatus] = useState<{
    tone: 'ok' | 'error'
    message: string
  } | null>(null)

  const [leadOpen, setLeadOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)

  const [leadName, setLeadName] = useState('')
  const [leadEmail, setLeadEmail] = useState('')
  const [leadCompany, setLeadCompany] = useState('')
  const [leadRole, setLeadRole] = useState('')
  const [leadSubmitting, setLeadSubmitting] = useState(false)
  const [leadStatus, setLeadStatus] = useState<{
    tone: 'ok' | 'error'
    message: string
  } | null>(null)

  const rootRef = useRef<HTMLDivElement>(null)
  const leadDialogRef = useRef<HTMLDivElement>(null)

  const prompt = PROMPT_LIBRARY[promptKey][market]
  const response = RESPONSE_LIBRARY[model]

  // Scroll reveal — visible by default for SSR/no-JS, then armed only after
  // the client confirms IntersectionObserver and motion are available.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const targets = Array.from(root.querySelectorAll<HTMLElement>('.reveal'))

    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    root.dataset.phase = 'armed'

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            io.unobserve(entry.target)
          }
        })
      },
      { rootMargin: '0px 0px -12% 0px' }
    )

    targets.forEach(el => io.observe(el))
    return () => {
      io.disconnect()
      delete root.dataset.phase
      targets.forEach(el => el.classList.remove('is-visible'))
    }
  }, [])

  // Dialogs close on Escape and trap focus loosely by returning it on close.
  useEffect(() => {
    if (!leadOpen && !reportOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLeadOpen(false)
        setReportOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [leadOpen, reportOpen])

  useEffect(() => {
    if (leadOpen) leadDialogRef.current?.focus()
  }, [leadOpen])

  const openLead = useCallback(() => {
    setLeadStatus(null)
    setLeadOpen(true)
  }, [])

  const handleAuditSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const value = canonicalizeDomainInput(domain)

    if (!value) {
      setDomainStatus({
        tone: 'error',
        message: 'أدخل نطاقًا صحيحًا مثل yourbrand.com.',
      })
      return
    }

    setDomainStatus({
      tone: 'ok',
      message: `اختياراتك جاهزة: ${MARKET_LABELS[market]}، ${LANGUAGE_LABELS[language]}، ${value}. أكمل بيانات التواصل لإرسال الطلب.`,
    })

    if (!leadCompany) setLeadCompany(`https://${value}`)
    openLead()
  }

  const handleLeadSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (leadSubmitting) return

    setLeadSubmitting(true)
    setLeadStatus(null)

    // The contact endpoint takes name / email / message, so the pilot
    // configuration is composed into the message rather than dropped.
    const message = [
      'طلب تدقيق تجريبي من صفحة Radar بالعربية.',
      `سوق التحليل: ${MARKET_LABELS[market]}`,
      `لغة التدقيق: ${LANGUAGE_LABELS[language]}`,
      `الموقع: ${leadCompany || domain || 'غير محدد'}`,
      `الدور: ${leadRole || 'غير محدد'}`,
    ].join('\n')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadName,
          email: leadEmail,
          subject: 'Radar Arabic pilot request',
          budget: 'Not sure yet',
          message,
        }),
      })

      if (res.ok) {
        setLeadStatus({
          tone: 'ok',
          message: 'وصلنا طلبك. سيتواصل معك الفريق لتأكيد نطاق التدقيق.',
        })
        setLeadName('')
        setLeadEmail('')
        setLeadRole('')
      } else {
        setLeadStatus({
          tone: 'error',
          message: 'تعذّر إرسال الطلب. حاول مرة أخرى أو راسلنا مباشرة.',
        })
      }
    } catch (error) {
      console.error('Arabic Radar lead request failed', error)
      setLeadStatus({
        tone: 'error',
        message: 'تعذّر الاتصال. تحقق من الشبكة وحاول مرة أخرى.',
      })
    } finally {
      setLeadSubmitting(false)
    }
  }

  return (
    <div className='radar-ar' data-theme='dark' ref={rootRef}>
      {/* This page has no global navigation by design: the Arabic wordmark
          returns home and the adjacent language link opens the equivalent
          English Radar page.

          Plain <a>, never next/link. `lang` and `dir` live on <html> and are
          resolved on the server from the request path, but a client-side
          transition keeps the same document — so a <Link> out of here would
          land on the English page still marked `dir="rtl"`. Crossing a
          language boundary is a document change, so it takes a document
          navigation. The same applies to any future link INTO /ar/*. */}
      <nav className='ar-return' aria-label='التنقل واختيار اللغة'>
        <a className='ar-wordmark' href={HOME_HREF} aria-label='Pixelmojo'>
          <Image
            src='/pixelmojo-branding-arabic.png'
            alt='Pixelmojo'
            width={1400}
            height={257}
            priority
          />
        </a>
        <a
          href={ENGLISH_RADAR_HREF}
          hrefLang='en'
          lang='en'
          dir='ltr'
          aria-label='View Radar in English'
        >
          <span aria-hidden='true'>←</span> English
        </a>
      </nav>

      <div>
        {/* ---------- Hero ---------- */}
        <section className='hero' id='top'>
          <div className='container hero-grid'>
            <div className='hero-copy'>
              <p className='eyebrow'>
                تدقيق تجريبي لحضور العلامة في إجابات الذكاء الاصطناعي
              </p>
              <h1>
                الذكاء الاصطناعي يتحدث عن علامتك.{' '}
                <span className='accent'>
                  <bdi dir='ltr'>Radar</bdi> يوضح ما يقوله ولماذا.
                </span>
              </h1>
              <p className='hero-subtitle'>
                اكتشف كيف تصف نماذج <bdi dir='ltr'>ChatGPT</bdi> و
                <bdi dir='ltr'>Claude</bdi> و<bdi dir='ltr'>Gemini</bdi> و
                <bdi dir='ltr'>Perplexity</bdi> علامتك بالعربية والإنجليزية، ضمن
                سياق السعودية أو الإمارات أو عرض إقليمي لدول مجلس التعاون.
              </p>
              <div className='hero-actions'>
                <button
                  className='button-yellow'
                  type='button'
                  onClick={openLead}
                >
                  اطلب تدقيقًا تجريبيًا{' '}
                  <span className='arrow' aria-hidden='true'>
                    ←
                  </span>
                </button>
                <button
                  className='button-outline'
                  type='button'
                  onClick={() => setReportOpen(true)}
                >
                  شاهد نموذج تقرير ثنائي اللغة
                </button>
              </div>
              <div className='hero-note' aria-label='مزايا البداية'>
                <span>ضبط سياق السوق واللغة</span>
                <span>دليل لكل نتيجة</span>
                <span>مراجعة يقدّمها الفريق</span>
              </div>
            </div>

            <div
              className='radar-stage'
              aria-label='معاينة توضيحية لتدقيق حضور العلامة'
            >
              <div className='radar-disc' aria-hidden='true'>
                <span className='radar-axis-h' />
                <span className='radar-axis-v' />
                <span className='radar-sweep' />
                <i className='blip one' />
                <i className='blip two' />
                <i className='blip three' />
                <i className='blip four' />
              </div>

              <article className='live-panel'>
                <div className='panel-bar'>
                  <span>AI PROMPT · AR-SA</span>
                  <span className='live'>PILOT SAMPLE</span>
                </div>
                <div className='live-content'>
                  <span className='query-label'>السؤال الجاري اختباره</span>
                  <p className='query-text'>
                    ما أفضل الشركات لمراقبة ظهور العلامة في إجابات الذكاء
                    الاصطناعي في السعودية؟
                  </p>
                  <div className='model-row'>
                    <span>4 models in pilot scope</span>
                    <span className='model-dots' aria-label='أربعة نماذج'>
                      <i />
                      <i />
                      <i />
                      <i />
                    </span>
                  </div>
                </div>
              </article>

              <article className='finding-panel'>
                <div className='finding-head'>
                  <span>مثال على نتيجة</span>
                  <span>SAMPLE · 01</span>
                </div>
                <div className='finding-body'>
                  <span className='finding-icon'>!</span>
                  <div>
                    <strong>الصورة تختلف بين العربية والإنجليزية</strong>
                    <p>
                      تظهر علامتك في الأسئلة الإنجليزية أكثر. وفي العربية تكرر
                      ظهور علامات أخرى مع صفحات أوضح صلةً بالسياق السعودي.
                    </p>
                  </div>
                </div>
                <div
                  className='signal-list'
                  aria-label='أدلة النتيجة التوضيحية'
                >
                  <div className='signal-item'>
                    <b>18%</b>
                    <span>حصة الذكر بين العلامات</span>
                  </div>
                  <div className='signal-item'>
                    <b>7 / 24</b>
                    <span>أسئلة ظهر فيها الاسم</span>
                  </div>
                  <div className='signal-item'>
                    <b>2</b>
                    <span>صفحات من موقعك مستشهد بها</span>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <div className='capability-strip container' aria-label='نطاق التغطية'>
          {CAPABILITIES.map(item => (
            <div className='capability' key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        {/* ---------- How ---------- */}
        <section className='section' id='how'>
          <div className='container'>
            <div className='section-heading reveal'>
              <div>
                <p className='section-kicker'>من الإشارة إلى الإجراء</p>
                <h2>
                  اضبط سوق التحليل{' '}
                  <span className='accent'>والسياق المحلي للأسئلة.</span>
                </h2>
              </div>
              <p>
                اختيار السوق يغيّر مكتبة الأسئلة والكيانات والمنافسين والنية
                المحلية التي نختبرها، ولا يعني تشغيل النموذج من موقع جغرافي
                مختلف.
              </p>
            </div>

            <div className='audit-layout reveal'>
              <form className='audit-setup' onSubmit={handleAuditSubmit}>
                <div className='card-index'>
                  <span>PILOT REQUEST · 01</span>
                  <span>يُراجع يدويًا</span>
                </div>
                <h3>جهّز طلب التدقيق التجريبي</h3>
                <p>
                  حدّد سوق التحليل ولغة التدقيق. يراجع الفريق السياق ويجهّز
                  مجموعة الأسئلة المناسبة.
                </p>

                <div className='form-group'>
                  <div className='form-label'>
                    <span>سوق التحليل</span>
                    <small>MARKET CONTEXT</small>
                  </div>
                  <div className='segment' aria-label='اختر سياق السوق للتحليل'>
                    {MARKET_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type='button'
                        aria-pressed={market === option.value}
                        onClick={() => setMarket(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='form-group'>
                  <div className='form-label'>
                    <span>لغة التدقيق</span>
                    <small>AUDIT LANGUAGE</small>
                  </div>
                  <div className='segment' aria-label='اختر لغة التدقيق'>
                    {LANGUAGE_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type='button'
                        aria-pressed={language === option.value}
                        onClick={() => setLanguage(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='form-group'>
                  <label className='form-label' htmlFor='domain'>
                    <span>رابط الموقع</span>
                    <small>DOMAIN</small>
                  </label>
                  <div className='url-field'>
                    <span dir='ltr'>https://</span>
                    <Input
                      id='domain'
                      name='domain'
                      type='text'
                      inputMode='url'
                      autoComplete='url'
                      placeholder='yourbrand.com'
                      dir='ltr'
                      tone='inverse'
                      fieldSize='lg'
                      invalid={domainStatus?.tone === 'error'}
                      value={domain}
                      onChange={e => setDomain(e.target.value)}
                      aria-describedby='domainHelp'
                    />
                  </div>
                </div>

                <Button
                  className='button-yellow audit-submit'
                  variant='ghost'
                  type='submit'
                >
                  متابعة طلب التدقيق{' '}
                  <span className='arrow' aria-hidden='true'>
                    ←
                  </span>
                </Button>
                <div
                  className='form-status'
                  id='domainHelp'
                  aria-live='polite'
                  data-tone={domainStatus?.tone}
                >
                  {domainStatus?.message}
                </div>
                <p className='method-note'>
                  <b>METHOD NOTE</b>
                  <br />
                  يضبط هذا الاختيار الأسئلة والكيانات والمنافسين والنية المحلية.
                  ولا يزعم أن إجابة النموذج صادرة من بيئة جغرافية سعودية أو
                  إماراتية.
                </p>
              </form>

              <div className='audit-steps'>
                {AUDIT_STEPS.map(step => (
                  <article className='audit-step' key={step.tag}>
                    <span className='step-signal'>{step.signal}</span>
                    <div>
                      <h4>{step.title}</h4>
                      <p>{step.copy}</p>
                    </div>
                    <span className='step-tag'>{step.tag}</span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Prompt lab ---------- */}
        <section className='section prompt-section' id='prompts'>
          <div className='container'>
            <div className='section-heading reveal'>
              <div>
                <p className='section-kicker'>مكتبة أسئلة عربية</p>
                <h2>
                  أسئلة كُتبت للسوق.
                  <span className='accent' style={{ display: 'block' }}>
                    وليست ترجمة حرفية.
                  </span>
                </h2>
              </div>
              <p>
                تبدأ المكتبة بأسئلة عربية فصيحة وطبيعية يراجعها مختصون باللغة
                والسوق، ثم نضيف صيغًا سعودية أو خليجية عندما يغيّر السياق المحلي
                طريقة السؤال.
              </p>
            </div>

            <div className='prompt-lab reveal'>
              <div className='prompt-toolbar'>
                <div
                  className='prompt-tabs'
                  role='tablist'
                  aria-label='فئات الأسئلة'
                >
                  {PROMPT_TABS.map(tab => (
                    <button
                      key={tab.value}
                      className='prompt-tab'
                      type='button'
                      role='tab'
                      aria-selected={promptKey === tab.value}
                      onClick={() => setPromptKey(tab.value)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className='prompt-count'>
                  SAMPLE PROMPT SET · HUMAN REVIEW
                </div>
              </div>

              <div className='prompt-grid'>
                <article className='prompt-query'>
                  <div className='lab-label'>
                    <span>السؤال · AR-SA</span>
                    <span className='market-badge'>
                      {MARKET_LABELS[market]}
                    </span>
                  </div>
                  <blockquote className='prompt-quote'>
                    {prompt.text}
                  </blockquote>
                  <div className='variant-note'>
                    <b>{prompt.code}</b>
                    <span>{prompt.note}</span>
                  </div>
                </article>

                <article className='prompt-response'>
                  <div className='lab-label'>
                    <span>الإجابة + الدليل</span>
                    <span>SAMPLE MODEL OUTPUT</span>
                  </div>
                  <div className='response-models' aria-label='اختر النموذج'>
                    {MODELS.map(name => (
                      <button
                        key={name}
                        className='response-model'
                        type='button'
                        aria-pressed={model === name}
                        onClick={() => setModel(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                  <p className='response-copy' dir='auto'>
                    {response.before}
                    <mark>{response.mark}</mark>
                    {response.after}
                  </p>
                  <div className='evidence-row'>
                    <span className='evidence-status'>● SAMPLE SOURCE</span>
                    <span className='evidence-url' dir='ltr'>
                      example.com/sa/ai-visibility-guide
                    </span>
                    <span className='evidence-score'>ILLUSTRATIVE</span>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Results ---------- */}
        <section className='section' id='results'>
          <div className='container'>
            <div className='section-heading reveal'>
              <div>
                <p className='section-kicker'>النتيجة ومعها الدليل</p>
                <h2>
                  راجع كل إجابة{' '}
                  <span className='accent'>والدليل وراء الاستنتاج.</span>
                </h2>
              </div>
              <p>
                شاهد السؤال العربي، نص إجابة النموذج، دليل الإشارة أو الاستشهاد،
                ثم قارن حصتك بمنافسيك ضمن سياق السوق نفسه.
              </p>
            </div>

            <div
              className='results-shell reveal'
              aria-label='معاينة توضيحية لنتائج Radar'
            >
              <aside className='results-side'>
                <div className='mini-brand'>
                  <span className='mini-mark' />
                  <span>Radar</span>
                </div>
                <div className='side-item active'>نظرة عامة</div>
                <div className='side-item'>الأسئلة</div>
                <div className='side-item'>الاستشهادات</div>
                <div className='side-item'>المنافسون</div>
                <div className='side-item'>خطة التحسين</div>
              </aside>

              <div className='results-main'>
                <div className='results-top'>
                  <div>
                    <strong dir='ltr'>yourbrand.com</strong>
                    <span dir='ltr'>SAUDI · AR + EN · AUDIT #0248</span>
                  </div>
                  <span className='complete-chip'>نموذج توضيحي</span>
                </div>

                <div className='results-content'>
                  <div className='score-grid'>
                    <div className='score-card'>
                      <span>حصة الذكر بين جميع العلامات</span>
                      <strong>18%</strong>
                    </div>
                    <div className='score-card'>
                      <span>أسئلة ظهر فيها اسم علامتك</span>
                      <strong>7 / 24</strong>
                    </div>
                    <div className='score-card'>
                      <span>صفحات من موقعك استُشهد بها</span>
                      <strong>2</strong>
                    </div>
                    <div className='score-card'>
                      <span>إجراءات ذات أولوية</span>
                      <strong>6</strong>
                    </div>
                  </div>

                  <div className='comparison'>
                    <div className='comparison-head'>
                      <h3>حصة الظهور ضمن الأسئلة المختبرة</h3>
                      <span>بيانات توضيحية · ليست نتيجة لعلامة حقيقية</span>
                    </div>
                    <table className='comparison-table'>
                      <thead>
                        <tr>
                          <th>العلامة</th>
                          <th>حصة الذكر بين العلامات</th>
                          <th>أسئلة ظهر فيها الاسم</th>
                          <th>صفحات الموقع المستشهد بها</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <bdi dir='ltr'>Your brand</bdi>
                          </td>
                          <td>
                            <span className='bar-cell'>
                              <i style={{ width: '18%' }} />
                              <b>18%</b>
                            </span>
                          </td>
                          <td dir='ltr'>7 / 24</td>
                          <td dir='ltr'>2</td>
                        </tr>
                        <tr>
                          <td>المنافس أ</td>
                          <td>
                            <span className='bar-cell'>
                              <i style={{ width: '42%' }} />
                              <b>42%</b>
                            </span>
                          </td>
                          <td dir='ltr'>16 / 24</td>
                          <td dir='ltr'>9</td>
                        </tr>
                        <tr>
                          <td>المنافس ب</td>
                          <td>
                            <span className='bar-cell'>
                              <i style={{ width: '31%' }} />
                              <b>31%</b>
                            </span>
                          </td>
                          <td dir='ltr'>12 / 24</td>
                          <td dir='ltr'>6</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Report ---------- */}
        <section className='section' id='report'>
          <div className='container report-layout'>
            <div className='report-copy reveal'>
              <p className='section-kicker'>نموذج التقرير التجريبي</p>
              <h2>
                تقرير واحد يفهمه فريقك{' '}
                <span style={{ color: 'var(--magenta)' }}>وعميلك.</span>
              </h2>
              <p>
                ضمن البرنامج التجريبي، يجهّز الفريق ملخصًا بالعربية والإنجليزية.
                تبقى الأقسام العربية RTL، بينما تحافظ الروابط وأسماء النماذج
                والبيانات الإنجليزية على اتجاهها الصحيح.
              </p>
              <ul className='report-checks'>
                <li>ملخص تنفيذي بالعربية والإنجليزية</li>
                <li>نتائج كل سؤال مع الاستشهادات والأدلة</li>
                <li>مقارنة ظهور المنافسين حسب السوق</li>
                <li>خطوات التحسين مرتبة حسب الأولوية</li>
                <li>نسخة PDF قابلة للحفظ والمشاركة</li>
              </ul>
              <div className='report-actions'>
                <button
                  className='button-yellow'
                  type='button'
                  onClick={() => window.print()}
                >
                  حفظ نموذج التقرير كملف PDF{' '}
                  <span className='arrow' aria-hidden='true'>
                    ↓
                  </span>
                </button>
                <button
                  className='button-outline'
                  type='button'
                  onClick={() => setReportOpen(true)}
                >
                  معاينة التقرير
                </button>
              </div>
            </div>

            <div className='report-stack reveal' aria-hidden='true'>
              <article className='report-sheet'>
                <div className='report-cover'>
                  <span className='report-wordmark'>
                    Radar <b>by Pixelmojo</b>
                  </span>
                  <span className='report-id'>AUDIT · AR-SA / EN-SA</span>
                </div>
                <div className='report-body'>
                  <small>AI VISIBILITY AUDIT · EXECUTIVE SUMMARY</small>
                  <h3>كيف تظهر علامتك في أسئلة موجّهة إلى السوق السعودي؟</h3>
                  <p>
                    لقطة ثنائية اللغة لظهور العلامة ودقة وصفها والمصادر المذكورة
                    في إجابات{' '}
                    <bdi dir='ltr'>
                      ChatGPT, Claude, Gemini &amp; Perplexity
                    </bdi>
                    .
                  </p>
                  <div className='report-score'>
                    <strong>
                      <b>18</b>%
                    </strong>
                    <p>
                      <b>حصة الظهور:</b> ظهرت علامتك في 7 من أصل 24 سؤالًا،
                      وكانت حصتها في العربية أقل منها في الإنجليزية.
                    </p>
                  </div>
                  <div className='report-columns'>
                    <div className='report-mini'>
                      <b>فرصة عالية الأثر</b>
                      <p>
                        أنشئ صفحة عربية واضحة للفئة مع تعريف دقيق للمنتج وأدلة
                        ثقة قابلة للاستشهاد.
                      </p>
                    </div>
                    <div className='report-mini' dir='ltr'>
                      <b>High-impact opportunity</b>
                      <p>
                        Publish an Arabic category page with a precise entity
                        description and citable local proof.
                      </p>
                    </div>
                    <div className='report-mini'>
                      <b>المنافسون</b>
                      <p>منافس أ 42% · منافس ب 31% · علامتك 18%</p>
                    </div>
                    <div className='report-mini' dir='ltr'>
                      <b>Evidence</b>
                      <p>
                        7/24 prompt appearances · 4 models · 2 cited site pages
                        · 6 priority actions
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ---------- FAQ ---------- */}
        <section className='section' id='faq'>
          <div className='container faq-grid'>
            <div className='faq-intro reveal'>
              <p className='section-kicker'>أسئلة قبل طلب التدقيق</p>
              <h2>إجابات قصيرة وواضحة.</h2>
              <p>عن اللغة والأسواق والنماذج وطريقة قراءة النتائج.</p>
            </div>

            <div className='faq-list reveal'>
              {FAQ_ITEMS.map(item => (
                <details key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Final CTA ---------- */}
        <section className='final-cta'>
          <div className='container cta-box reveal'>
            <div>
              <p className='section-kicker'>برنامج التدقيق العربي التجريبي</p>
              <h2>
                اطلب تدقيقًا تجريبيًا <span>لسوقك.</span>
              </h2>
              <p>
                سنراجع ظهور علامتك في أسئلة عربية وإنجليزية مضبوطة على السعودية
                أو الإمارات أو عرض إقليمي لدول مجلس التعاون، ثم نشاركك تقريرًا
                أوليًا بالأدلة.
              </p>
            </div>
            <div className='cta-actions'>
              <button
                className='button-yellow'
                type='button'
                onClick={openLead}
              >
                اطلب التدقيق التجريبي{' '}
                <span className='arrow' aria-hidden='true'>
                  ←
                </span>
              </button>
            </div>
          </div>
        </section>

        <div className='ar-foot'>
          <span>تراعي هذه الصفحة اتجاه النص العربي والمحتوى المختلط.</span>
          <div className='ar-foot-actions'>
            <button type='button' onClick={reopenConsentBanner}>
              إعدادات ملفات تعريف الارتباط
            </button>
            {/* Document navigation, same reason as the header link above. */}
            <a href={ENGLISH_RADAR_HREF} hrefLang='en' lang='en' dir='ltr'>
              English
            </a>
          </div>
        </div>
      </div>

      {/* ---------- Report preview dialog ---------- */}
      {reportOpen && (
        <div
          className='ar-dialog-backdrop'
          role='presentation'
          onClick={() => setReportOpen(false)}
        >
          <div
            className='ar-dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='reportDialogTitle'
            onClick={e => e.stopPropagation()}
          >
            <div className='dialog-head'>
              <strong id='reportDialogTitle'>معاينة التقرير ثنائي اللغة</strong>
              <button
                className='dialog-close'
                type='button'
                onClick={() => setReportOpen(false)}
                aria-label='إغلاق'
              >
                ×
              </button>
            </div>
            <div className='dialog-body'>
              <div className='modal-report'>
                <div className='modal-report-head'>
                  <strong>Radar by Pixelmojo</strong>
                  <span>AR-SA / EN-SA · SAMPLE AUDIT</span>
                </div>
                <h3>ملخص ظهور العلامة في إجابات الذكاء الاصطناعي</h3>
                <p>السعودية · العربية + الإنجليزية · بيانات توضيحية للمعاينة</p>
                <div className='modal-bilingual'>
                  <article>
                    <h4>الخلاصة التنفيذية</h4>
                    <p>
                      شكّلت علامتك 18% من إجمالي ذكر العلامات، وظهرت في 7 من أصل
                      24 سؤالًا. تكرر الاستشهاد بصفحات منافسين ذات سياق محلي
                      أوضح.
                    </p>
                  </article>
                  <article dir='ltr' lang='en'>
                    <h4>Executive finding</h4>
                    <p>
                      Your brand accounted for 18% of all brand mentions and
                      appeared in 7 of 24 prompts. Competitor pages with clearer
                      local context were cited repeatedly.
                    </p>
                  </article>
                </div>
              </div>
            </div>
            <div className='modal-actions'>
              <button
                className='button-yellow'
                type='button'
                onClick={() => {
                  setReportOpen(false)
                  openLead()
                }}
              >
                اطلب تدقيقًا تجريبيًا
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Lead dialog ---------- */}
      {leadOpen && (
        <div
          className='ar-dialog-backdrop'
          role='presentation'
          onClick={() => setLeadOpen(false)}
        >
          <div
            className='ar-dialog'
            role='dialog'
            aria-modal='true'
            aria-labelledby='leadDialogTitle'
            tabIndex={-1}
            ref={leadDialogRef}
            onClick={e => e.stopPropagation()}
          >
            <div className='dialog-head'>
              <strong id='leadDialogTitle'>اطلب التدقيق التجريبي</strong>
              <button
                className='dialog-close'
                type='button'
                onClick={() => setLeadOpen(false)}
                aria-label='إغلاق'
              >
                ×
              </button>
            </div>
            <div className='dialog-body'>
              <form className='lead-form' onSubmit={handleLeadSubmit}>
                <label htmlFor='lead-name'>الاسم</label>
                <Input
                  id='lead-name'
                  name='name'
                  type='text'
                  required
                  minLength={2}
                  tone='inverse'
                  fieldSize='lg'
                  value={leadName}
                  onChange={e => setLeadName(e.target.value)}
                />

                <label htmlFor='lead-email'>البريد الإلكتروني</label>
                <Input
                  id='lead-email'
                  name='email'
                  type='email'
                  required
                  dir='ltr'
                  tone='inverse'
                  fieldSize='lg'
                  value={leadEmail}
                  onChange={e => setLeadEmail(e.target.value)}
                />

                <label htmlFor='lead-company'>رابط الموقع</label>
                <Input
                  id='lead-company'
                  name='company'
                  type='text'
                  dir='ltr'
                  placeholder='https://yourbrand.com'
                  tone='inverse'
                  fieldSize='lg'
                  value={leadCompany}
                  onChange={e => setLeadCompany(e.target.value)}
                />

                <label htmlFor='lead-role'>الدور</label>
                <select
                  id='lead-role'
                  name='role'
                  className={marketingFieldClasses({
                    tone: 'inverse',
                    size: 'lg',
                  })}
                  value={leadRole}
                  onChange={e => setLeadRole(e.target.value)}
                >
                  <option value=''>اختر الدور</option>
                  <option value='marketing'>التسويق</option>
                  <option value='founder'>مؤسس / إدارة</option>
                  <option value='agency'>وكالة</option>
                  <option value='other'>أخرى</option>
                </select>

                <Button
                  className='button-yellow'
                  variant='ghost'
                  type='submit'
                  disabled={leadSubmitting}
                  aria-busy={leadSubmitting}
                >
                  {leadSubmitting ? 'جارٍ الإرسال…' : 'إرسال الطلب'}
                </Button>

                <p className='privacy-note'>
                  نستخدم بياناتك للتواصل بشأن هذا الطلب فقط. اطّلع على{' '}
                  <a href='/privacy-policy' hrefLang='en'>
                    سياسة الخصوصية
                  </a>
                  .
                </p>

                <div
                  className='lead-status'
                  aria-live='polite'
                  data-tone={leadStatus?.tone}
                >
                  {leadStatus?.message}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
